import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Divider, Button } from '@material-ui/core';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import classNames from 'classnames';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { postLCAnswerAPIcall } from '../../../redux/actions/training/training.actions';
import {
  white,
  dividerGrey,
  universalStyles,
  charcoalGrey,
  poslumaBlue,
  darkGrey,
  yellow,
  poslumaBox,
} from '../../../app/theme';
import { LearningCheckFormat, OPTIONS } from '../../../constants/learningCheck.constants';
import { BASE_URL } from '../../../constants/paths.constants';
import AppState from '../../../models/redux/state/app.state';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  axuminBox: {
    backgroundColor: charcoalGrey,
    paddingTop: 49,
    paddingBottom: 50,
    paddingLeft: 64,
    paddingRight: 64,
  },
  poslumaBox: {
    backgroundColor: poslumaBox,
    paddingTop: 49,
    paddingBottom: 50,
    paddingLeft: 64,
    paddingRight: 64,
  },
  divider: {
    marginTop: 25,
    marginBottom: 32,
    background: dividerGrey,
    height: 2,
  },
  questionRow: {
    paddingTop: 23,
    display: 'flex',
  },
  number: {
    color: white,
    paddingRight: 15,
    fontWeight: 'bold',
  },
  question: {
    fontSize: 20,
    color: white,
    paddingBottom: 8,
    '& > p': {
      margin: 0,
    },
  },
  label: {
    paddingLeft: 20,
    display: 'flex',
    flexDirection: 'row',
    marginTop: 7,
  },
  optionIndent: {
    display: 'block !important',
  },
  boldText: {
    display: 'inline',
    paddingRight: 8,
    color: white,
    fontWeight: 600,
  },
  correctAnswerAxumin: {
    display: 'inline',
    paddingRight: 8,
    color: yellow,
    fontWeight: 600,
  },
  correctAnswerPosluma: {
    display: 'inline',
    paddingRight: 8,
    color: poslumaBlue,
    fontWeight: 600,
  },
  yellowText: {
    color: yellow,
  },
  regularText: {
    color: white,
  },
  correctText: {
    fontSize: 20,
    display: 'inline',
    color: white,
    '& > p': {
      display: 'inline',
    },
  },
  correctCustomText: {
    fontSize: 20,
    display: 'inline',
    color: yellow,
    '& > p': {
      display: 'inline',
    },
  },
  controls: {
    '& > label': {
      alignItems: 'flex-start',
    },
  },
  buttonRow: {
    marginTop: 25,
  },
  button: {
    width: 170,
    height: 41,
    fontSize: 16,
    letterSpacing: 0,
    fontWeight: 600,
    borderRadius: 24,
    color: darkGrey,
  },
  answer: {
    marginTop: 25,
  },
  imageRow: {
    maxWidth: '70%',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 40,
  },
  imageWrapper: {
    flex: 1,
    marginLeft: 57,
    '&:nth-of-type(1)': {
      margin: 0,
    },
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  caption: {
    color: white,
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    maxWidth: '80%',
    margin: 'auto',
  },
}));

interface SingleQuestionProps {
  thisQuestion: LearningCheckFormat;
  clickedCheckAnswer: (clicked: boolean) => void;
  module: string;
  page: number;
}

const SingleQuestion = ({ thisQuestion, clickedCheckAnswer, module, page }: SingleQuestionProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [value, setValue] = useState('');
  const [checkedAnswer, setCheckedAnswer] = useState(false);
  const trainingInfo = useSelector((state: AppState) => state.training.current);
  const link = window.location.href;
  let theme = null;
  if (link.includes('posluma')) {
    theme = '1';
  } else {
    theme = '2';
  }

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setValue(event.target.value as string);
  };

  const { number, question, options, answerIds, correct, optionsUnlabeled, childNode } = thisQuestion;
  const handleClickCheckAnswer = () => {
    setCheckedAnswer(true);
    clickedCheckAnswer(true);

    const currentTraining =
      trainingInfo && trainingInfo.trainings.filter((training) => module.includes(training.training.name));
    const trainingUrl = currentTraining && currentTraining[0].url;
    const dateCompleted = currentTraining && currentTraining[0].dateCompleted;
    const currentPage = currentTraining && currentTraining[0].currentPage;

    // We only report the answer to the back end if they have not yet completed this module
    // and they have not yet passed this page
    if (!dateCompleted && currentPage === page) {
      const answerIndex = OPTIONS.indexOf(value);
      const answer = answerIds && answerIds[answerIndex];
      dispatch(postLCAnswerAPIcall(trainingUrl, answer));
    }
  };

  const { questionText, questionImages, questionCaptions } = question;
  const { option, correctText, images, captions, customText } = correct;
  const DynamicComponent = childNode;

  return (
    <div>
      <Divider className={classes.divider} />
      {DynamicComponent && <DynamicComponent setSelectedAnswer={(canCheck) => setValue(canCheck ? 'A' : '')} />}
      {!DynamicComponent && (
        <div className={theme === '1' ? classes.poslumaBox : classes.axuminBox}>
          <Typography variant="h6">Select the correct response</Typography>
          <div className={classes.questionRow}>
            <Typography variant="body2" className={classes.number}>{`${number}.`}</Typography>
            <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.question}>
              {questionText}
            </ReactMarkdown>
          </div>
          {questionImages && (
            <div className={classes.imageRow}>
              {questionImages.map((image, index) => (
                <div className={classes.imageWrapper} key={image.path}>
                  <img className={classes.image} src={`${BASE_URL}${image.path}`} alt={image.alt} />
                  {questionCaptions && (
                    <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.caption}>
                      {questionCaptions[index]}
                    </ReactMarkdown>
                  )}
                </div>
              ))}
            </div>
          )}
          <FormControl component="fieldset">
            <RadioGroup value={value} onChange={handleChange} className={classes.controls}>
              {options?.length > 0 &&
                options.map((optionItem, index) => {
                  const label = (
                    <div className={classes.label}>
                      {!optionsUnlabeled && (
                        <Typography variant="body1" className={classNames(classes.optionIndent, classes.boldText)}>
                          {`${OPTIONS[index]}. `}
                        </Typography>
                      )}
                      <Typography
                        variant="body1"
                        className={classNames(classes.regularText, optionsUnlabeled ? classes.boldText : '')}
                      >
                        {optionItem}
                      </Typography>
                    </div>
                  );
                  return (
                    <FormControlLabel
                      key={optionItem}
                      value={OPTIONS[index]}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  );
                })}
            </RadioGroup>
          </FormControl>
        </div>
      )}
      <div className={classes.buttonRow}>
        <Button
          variant="contained"
          color="primary"
          disabled={!value || checkedAnswer}
          className={classes.button}
          onClick={handleClickCheckAnswer}
        >
          CHECK ANSWER
        </Button>
      </div>
      {checkedAnswer && value && (
        <>
          <div className={classes.answer}>
            <span>
              {customText ? (
                <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.correctCustomText}>
                  {customText}
                </ReactMarkdown>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    className={theme === '1' ? classes.correctAnswerPosluma : classes.correctAnswerAxumin}
                  >
                    {theme === '1' ? 'The correct answer is' : 'Correct answer is'}
                    {`${optionsUnlabeled && !correctText?.length ? `: ${option}` : ` ${option}: `}`}
                  </Typography>
                  {correctText?.length > 0 && (
                    <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.correctText}>
                      {correctText}
                    </ReactMarkdown>
                  )}
                </>
              )}
            </span>
          </div>
          {images && (
            <div className={classes.imageRow}>
              {images.map((image, index) => (
                <div className={classes.imageWrapper} key={image.path}>
                  <img className={classes.image} src={`${BASE_URL}${image.path}`} alt={image.alt} />
                  {captions && (
                    <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.caption}>
                      {captions[index]}
                    </ReactMarkdown>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SingleQuestion;
